import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Warning, Link as LinkIcon, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ThirdPartyDeliveryCard — live config surface for a third-party
 * delivery brand (DoorDash, Uber Eats, Grubhub).
 *
 * Loads/saves credentials via /api/store-settings, and (for DoorDash)
 * fires a live sandbox round-trip via /api/doordash/test-order to prove
 * keys work end-to-end.
 */
const ThirdPartyDeliveryCard = ({
  brand,
  brandKey,       // "dd" | "ue" | "gh" — settings-field prefix
  logoBg = "bg-red-50",
  logoColor = "text-red-600",
  applyUrl,
  applyLabel,
  productLine,
  credentials, // [{ key, label, placeholder, secret? }]
  notes,
  // Optional test-order endpoint (only DoorDash has one implemented).
  testEndpoint,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [environment, setEnvironment] = useState("sandbox");
  const [values, setValues] = useState({});
  const [connected, setConnected] = useState(false);

  const fieldKey = (k) => `${brandKey}_${k}`;

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/store-settings`);
        if (ignore) return;
        setEnabled(!!data[`${brandKey}_enabled`]);
        setEnvironment(data[`${brandKey}_environment`] || "sandbox");
        const init = {};
        credentials.forEach((c) => {
          init[c.key] = data[fieldKey(c.key)] || "";
        });
        setValues(init);
        // "connected" = has the minimum required creds set
        const required = credentials.filter((c) => c.required !== false).slice(0, 3);
        const allSet = required.every((c) => (data[fieldKey(c.key)] || "").length > 0);
        setConnected(allSet);
      } catch (err) {
        console.error(err);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [brandKey]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        [`${brandKey}_enabled`]: enabled,
        [`${brandKey}_environment`]: environment,
      };
      credentials.forEach((c) => {
        payload[fieldKey(c.key)] = (values[c.key] || "").trim();
      });
      await axios.put(`${API}/api/store-settings`, payload);
      toast.success(`${brand} credentials saved`);
      const required = credentials.filter((c) => c.required !== false).slice(0, 3);
      const allSet = required.every((c) => (values[c.key] || "").trim().length > 0);
      setConnected(allSet);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to save ${brand} credentials`);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEndpoint) {
      toast.info(`${brand} integration is coming soon. Only DoorDash has live sandbox testing today.`);
      return;
    }
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}${testEndpoint}`);
      toast.success(data.message || "Test order successful");
    } catch (err) {
      const raw = err?.response?.data?.detail || err.message || "Test order failed";
      // DoorDash returns JSON strings inside the detail field — surface them cleanly.
      let msg = raw;
      try { msg = JSON.parse(raw).message || raw; } catch { /* not JSON */ }
      toast.error(msg, { duration: 10000 });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card data-testid={`${brand.toLowerCase().replace(/\s+/g, "-")}-info-card`}>
        <CardHeader className="flex-row items-center gap-3 space-y-0">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${logoBg} ${logoColor} font-heading font-medium`}>
            {brand.slice(0, 2)}
          </div>
          <div className="flex-1">
            <CardTitle className="font-heading">{brand}</CardTitle>
            <CardDescription>{productLine}</CardDescription>
          </div>
          {connected ? (
            <Badge className="text-xs bg-green-600 hover:bg-green-600" data-testid={`${brandKey}-status-badge`}>
              <CheckCircle size={12} weight="fill" className="mr-1" /> Connected
            </Badge>
          ) : (
            <Badge variant="outline" className="text-xs" data-testid={`${brandKey}-status-badge`}>
              Not connected
            </Badge>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Notice — dark text for contrast on both light and dark backgrounds */}
          <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-sm">
            <Warning size={18} weight="fill" className="text-amber-600 dark:text-amber-300 shrink-0 mt-0.5" />
            <div className="text-amber-900 dark:text-amber-100">
              <p className="font-medium mb-0.5">You&apos;ll need a partner account before this can connect</p>
              <p className="text-xs opacity-80">
                Save the credentials below once you&apos;re approved. The badge flips to &quot;Connected&quot; and orders will start flowing to your KDS.
              </p>
            </div>
          </div>

          {applyUrl && (
            <a
              href={applyUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
              data-testid={`${brandKey}-apply-link`}
            >
              <LinkIcon size={14} weight="bold" /> {applyLabel}
            </a>
          )}

          {/* Enable + Environment row */}
          <div className="flex flex-wrap items-center gap-6 p-3 rounded-lg bg-muted/40 border">
            <div className="flex items-center gap-3">
              <Switch
                checked={enabled}
                onCheckedChange={setEnabled}
                disabled={loading}
                data-testid={`${brandKey}-enabled-switch`}
              />
              <div>
                <Label className="text-sm">Enable {brand}</Label>
                <p className="text-xs text-muted-foreground">When on, the POS uses these credentials to dispatch deliveries.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Label className="text-xs">Environment</Label>
              <div className="inline-flex rounded-md border p-0.5" role="group">
                <button
                  type="button"
                  onClick={() => setEnvironment("sandbox")}
                  disabled={loading}
                  className={`px-3 py-1 text-xs rounded ${environment === "sandbox" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                  data-testid={`${brandKey}-env-sandbox-btn`}
                >
                  Sandbox
                </button>
                <button
                  type="button"
                  onClick={() => setEnvironment("production")}
                  disabled={loading}
                  className={`px-3 py-1 text-xs rounded ${environment === "production" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                  data-testid={`${brandKey}-env-prod-btn`}
                >
                  Production
                </button>
              </div>
            </div>
          </div>

          {/* Credential inputs — always editable */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {credentials.map((c) => (
              <div key={c.key} className="space-y-1">
                <Label className="text-xs flex items-center gap-1.5">
                  {c.label}
                  {c.required === false && (
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-1.5 py-0.5 rounded">Optional</span>
                  )}
                </Label>
                <Input
                  type={c.secret ? "password" : "text"}
                  placeholder={c.placeholder}
                  value={values[c.key] || ""}
                  onChange={(e) => setValues((prev) => ({ ...prev, [c.key]: e.target.value }))}
                  disabled={loading}
                  data-testid={`${brandKey}-${c.key}-input`}
                />
                {c.hint && <p className="text-[11px] text-muted-foreground">{c.hint}</p>}
              </div>
            ))}
          </div>

          {notes && (
            <div className="text-xs text-muted-foreground">
              {notes}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={handleSave}
              disabled={loading || saving}
              data-testid={`${brandKey}-save-btn`}
            >
              {saving ? "Saving…" : "Save Credentials"}
            </Button>
            <Button
              variant="outline"
              onClick={handleTest}
              disabled={loading || testing || !connected}
              data-testid={`${brandKey}-test-btn`}
            >
              <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />
              {testing ? "Sending…" : "Send Test Order"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const DoorDashCard = () => (
  <ThirdPartyDeliveryCard
    brand="DoorDash"
    brandKey="dd"
    logoBg="bg-red-50 dark:bg-red-500/10"
    logoColor="text-red-600 dark:text-red-400"
    applyUrl="https://developer.doordash.com/"
    applyLabel="Open DoorDash Developer Console"
    productLine="Drive (your website, their drivers) + Marketplace (their app, orders drop into your POS)"
    credentials={[
      { key: "developer_id",   label: "Developer ID",   placeholder: "abc123-4de5-…" },
      { key: "key_id",         label: "Key ID",         placeholder: "key_id from console" },
      { key: "signing_secret", label: "Signing Secret", placeholder: "•••••••••••••", secret: true },
      { key: "webhook_token",  label: "Webhook Bearer Token", placeholder: "shared secret for /api/doordash/webhook", secret: true, required: false, hint: "Optional — a random string YOU pick and paste into DoorDash's Webhook settings so DoorDash can push live status updates." },
    ]}
    notes="Drive uses a short-lived JWT signed with your signing_secret per API call. Send Test Order fires a live sandbox quote — no delivery is created and no money moves."
    testEndpoint="/api/doordash/test-order"
  />
);

export const UberEatsCard = () => (
  <ThirdPartyDeliveryCard
    brand="Uber Eats"
    brandKey="ue"
    logoBg="bg-black/5 dark:bg-white/10"
    logoColor="text-neutral-900 dark:text-neutral-100"
    applyUrl="https://developer.uber.com/products/"
    applyLabel="Open Uber Developer Portal"
    productLine="Uber Direct — your website, Uber's drivers. No app listing, no commissions."
    credentials={[
      { key: "client_id",       label: "Client ID",     placeholder: "app-client-id" },
      { key: "client_secret",   label: "Client Secret", placeholder: "•••••••••••••", secret: true },
      { key: "customer_id",     label: "Customer ID",   placeholder: "UUID from Uber Direct dashboard" },
      { key: "webhook_secret",  label: "Webhook HMAC Secret", placeholder: "shared secret Uber provides per merchant", secret: true, required: false, hint: "Optional — Uber issues this per-merchant so we can verify their webhook pushes." },
    ]}
    notes="OAuth 2.0 client-credentials with scope 'eats.deliveries'. Token cached ~30 days. Send Test Order fires a live sandbox quote between two SF test addresses."
    testEndpoint="/api/uber/test-order"
  />
);

export const GrubhubCard = () => (
  <ThirdPartyDeliveryCard
    brand="Grubhub"
    brandKey="gh"
    logoBg="bg-orange-50 dark:bg-orange-500/10"
    logoColor="text-orange-600 dark:text-orange-400"
    applyUrl="https://get.grubhub.com/grubhub-connect/"
    applyLabel="Apply for Grubhub Connect"
    productLine="Grubhub Connect — orders from the Grubhub / Seamless apps drop into your KDS."
    credentials={[
      { key: "client_id",     label: "Client ID",     placeholder: "OAuth client id" },
      { key: "client_secret", label: "Client Secret", placeholder: "•••••••••••••", secret: true },
      { key: "restaurant_id", label: "Restaurant ID", placeholder: "per store" },
    ]}
    notes="Grubhub Connect uses OAuth 2.0 client-credentials. Order events arrive via HMAC-signed webhooks. Live dispatch coming soon."
  />
);

export default ThirdPartyDeliveryCard;
